import HealthSourceInfo from "@/components/health/HealthSourceInfo";
import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  X,
  Play,
  Pause,
  ChevronLeft,
  Plus,
  Minus,
  CheckCircle2,
  Clock,
  Flame,
  Dumbbell,
  FastForward,
  Trophy,
} from "lucide-react";
import confetti from "canvas-confetti";
import { useAuth } from "@/lib/AuthContext";
import {
  useActiveWorkout,
  formatElapsed,
  estimateCaloriesBurned,
  normalizeExercises,
} from "@/lib/ActiveWorkoutContext";
import { base44 } from "@/api/base44Client";
import { format } from "date-fns";

/**
 * Clean Web Audio synthesis for timer chime — no external asset dependencies.
 */
function playRestAlertChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    const now = ctx.currentTime;
    osc.frequency.setValueAtTime(880, now); // A5
    osc.frequency.exponentialRampToValueAtTime(1320, now + 0.15); // E6
    gain.gain.setValueAtTime(0.25, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    osc.start(now);
    osc.stop(now + 0.35);
  } catch (_e) {
    /* audio context not permitted or suspended */
  }
}

function triggerHaptic() {
  try {
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate([100, 50, 100]);
    }
  } catch (_e) {
    /* ignore */
  }
}

function parseInstructions(exercise) {
  if (!exercise) return [];
  let list = [];
  if (Array.isArray(exercise.instructions) && exercise.instructions.length > 0) {
    list = exercise.instructions;
  } else if (typeof exercise.instructions === "string" && exercise.instructions.trim()) {
    try {
      const parsed = JSON.parse(exercise.instructions);
      if (Array.isArray(parsed)) list = parsed;
      else list = [exercise.instructions];
    } catch {
      list = exercise.instructions.split(/\n+/).map((s) => s.trim()).filter(Boolean);
    }
  } else if (exercise.description && typeof exercise.description === "string") {
    list = exercise.description.split(/\n+/).map((s) => s.trim()).filter(Boolean);
  }

  return list
    .map((step) => {
      if (typeof step !== "string") return String(step || "");
      return step.replace(/^(?:Step\s*[:#]?\s*\d+\s*[:\-.]?\s*|\d+[\.\):]\s*)/i, "").trim();
    })
    .filter(Boolean);
}

export default function FullScreenActiveWorkout({ allExercises = [] }) {
  const { user } = useAuth();
  const {
    active,
    elapsedSeconds,
    saveAndExit,
    endWorkout,
    togglePauseTimer,
    updateActiveState,
    completeCurrentSet,
    skipRest,
    addRestSeconds,
    goToNextExercise,
    goToPreviousExercise,
  } = useActiveWorkout();

  const [confirmExitOpen, setConfirmExitOpen] = useState(false);
  const [completedSummary, setCompletedSummary] = useState(null);
  const [weight, setWeight] = useState(135);
  const [reps, setReps] = useState(10);
  const [restSecondsRemaining, setRestSecondsRemaining] = useState(0);

  // Directly query the exercise library so we never rely on parent prop availability
  const { data: catalogue = [] } = useQuery({
    queryKey: ["exercises-catalogue"],
    queryFn: () => base44.entities.Exercises.list("name", 2500),
    staleTime: 1000 * 60 * 60,
  });

  const mergedCatalogue = useMemo(() => {
    if (allExercises && allExercises.length > 0) return allExercises;
    return catalogue;
  }, [allExercises, catalogue]);

  const rawExercises = active?.plan?.exercises || active?.exercises || [];
  const [fetchedExercises, setFetchedExercises] = useState([]);

  // Auto-heal: If active.plan.exercises is missing/empty, fetch the plan from DB
  useEffect(() => {
    const directList = normalizeExercises(rawExercises);
    if (directList.length === 0 && active?.plan_id) {
      base44.entities.WorkoutPlan.get(active.plan_id)
        .then((p) => {
          if (p?.exercises) {
            const parsed = normalizeExercises(p.exercises);
            if (parsed.length > 0) {
              setFetchedExercises(parsed);
              if (updateActiveState) {
                updateActiveState((prev) => ({
                  ...prev,
                  plan: { ...(prev?.plan || {}), ...p, exercises: parsed },
                  exercises: parsed,
                  total_exercises: parsed.length,
                }));
              }
            }
          }
        })
        .catch(() => {
          base44.entities.WorkoutTemplate.get(active.plan_id)
            .then((t) => {
              if (t?.exercises) {
                const parsed = normalizeExercises(t.exercises);
                if (parsed.length > 0) {
                  setFetchedExercises(parsed);
                  if (updateActiveState) {
                    updateActiveState((prev) => ({
                      ...prev,
                      plan: { ...(prev?.plan || {}), ...t, exercises: parsed },
                      exercises: parsed,
                      total_exercises: parsed.length,
                    }));
                  }
                }
              }
            })
            .catch(() => {});
        });
    }
  }, [active?.plan_id, rawExercises, updateActiveState]);

  const exercises = useMemo(() => {
    const direct = normalizeExercises(rawExercises);
    return direct.length > 0 ? direct : fetchedExercises;
  }, [rawExercises, fetchedExercises]);

  const totalExercises = exercises.length || active?.total_exercises || 1;
  const currentExerciseIdx = Math.min(
    Math.max(0, totalExercises - 1),
    Math.max(0, active?.current_exercise_idx || 0)
  );
  const currentExercise = exercises[currentExerciseIdx] || null;

  // Resolve matching full exercise from catalogue using 3-tier matching
  const fullExercise = useMemo(() => {
    if (!currentExercise) return null;
    const name = (currentExercise.name || "").toLowerCase().trim();
    if (!name) return null;

    // 1. Direct match
    let found = mergedCatalogue.find(
      (e) => (e.name || "").toLowerCase().trim() === name
    );
    if (found) return found;

    // 2. Normalized match (dashes, parentheses, extra whitespace)
    const clean = (s) =>
      s
        .toLowerCase()
        .replace(/[-_]/g, " ")
        .replace(/[()]/g, "")
        .replace(/\s+/g, " ")
        .trim();
    const cleanCur = clean(name);
    found = mergedCatalogue.find((e) => clean(e.name || "") === cleanCur);
    if (found) return found;

    // 3. Substring match
    found = mergedCatalogue.find((e) => {
      const cleanE = clean(e.name || "");
      return cleanE && (cleanE.includes(cleanCur) || cleanCur.includes(cleanE));
    });
    return found || null;
  }, [currentExercise, mergedCatalogue]);

  const gifUrl = useMemo(() => {
    return (
      currentExercise?.gifUrl ||
      currentExercise?.gif_url ||
      currentExercise?.media ||
      currentExercise?.video_url ||
      fullExercise?.gifUrl ||
      fullExercise?.gif_url ||
      fullExercise?.media ||
      fullExercise?.video_url ||
      null
    );
  }, [currentExercise, fullExercise]);

  const instructions = useMemo(() => {
    const fromCur = parseInstructions(currentExercise);
    if (fromCur.length > 0) return fromCur;
    return parseInstructions(fullExercise);
  }, [currentExercise, fullExercise]);

  const targetMuscles = useMemo(() => {
    const fromCur = Array.isArray(currentExercise?.targetMuscles)
      ? currentExercise.targetMuscles
      : typeof currentExercise?.muscles_primary === "string"
      ? currentExercise.muscles_primary.split(/,\s*/).filter(Boolean)
      : [];
    if (fromCur.length > 0) return fromCur;
    if (Array.isArray(fullExercise?.targetMuscles)) return fullExercise.targetMuscles;
    if (typeof fullExercise?.muscles_primary === "string") {
      return fullExercise.muscles_primary.split(/,\s*/).filter(Boolean);
    }
    return [];
  }, [currentExercise, fullExercise]);

  // Responsive typography and spacing for instructions: wraps cleanly without blank space
  const instructionsTypography = useMemo(() => {
    const count = instructions.length;
    if (count <= 3) {
      return {
        fontSize: "clamp(0.88rem, 1.3vh + 0.1rem, 1.05rem)",
        lineHeight: "1.48",
        gap: "clamp(8px, 1.2vh, 14px)",
        padding: "clamp(10px, 1.6vh, 16px)",
      };
    }
    if (count <= 5) {
      return {
        fontSize: "clamp(0.82rem, 1.15vh + 0.08rem, 0.96rem)",
        lineHeight: "1.42",
        gap: "clamp(6px, 1vh, 10px)",
        padding: "clamp(8px, 1.4vh, 14px)",
      };
    }
    // 6+ steps
    return {
      fontSize: "clamp(0.78rem, 1vh + 0.06rem, 0.9rem)",
      lineHeight: "1.36",
      gap: "clamp(4px, 0.8vh, 8px)",
      padding: "clamp(8px, 1.2vh, 12px)",
    };
  }, [instructions.length]);

  const currentSet = active?.current_set || 1;
  const targetSets = Number(currentExercise?.sets) || 3;
  const prescribedReps = currentExercise?.reps || "8-12";
  const configuredRestSeconds = Number(currentExercise?.rest_seconds) || 60;

  // Keep default weight/reps synced when exercise or set changes
  useEffect(() => {
    if (currentExercise) {
      const parsedReps = parseInt(String(prescribedReps), 10);
      if (!Number.isNaN(parsedReps) && parsedReps > 0) {
        setReps(parsedReps);
      }
      // Check if previous set has a logged weight
      const completedList = active?.completed_sets?.[currentExerciseIdx] || [];
      const lastCompleted = completedList[completedList.length - 1];
      if (lastCompleted && typeof lastCompleted.weight === "number") {
        setWeight(lastCompleted.weight);
      }
    }
  }, [currentExerciseIdx, currentExercise, prescribedReps]);

  // REST COUNTDOWN TICKER
  const restTimer = active?.rest_timer;
  const isResting = Boolean(restTimer && restTimer.active);

  useEffect(() => {
    if (!isResting || !restTimer?.ends_at) {
      setRestSecondsRemaining(0);
      return;
    }

    const updateRest = () => {
      const remaining = Math.max(0, Math.ceil((restTimer.ends_at - Date.now()) / 1000));
      setRestSecondsRemaining(remaining);
      if (remaining <= 0) {
        playRestAlertChime();
        triggerHaptic();
        skipRest();
      }
    };

    updateRest();
    const intervalId = setInterval(updateRest, 500);
    return () => clearInterval(intervalId);
  }, [isResting, restTimer?.ends_at, skipRest]);

  // Weight / Reps Steppers
  const handleWeightDelta = (delta) => {
    setWeight((prev) => Math.max(0, prev + delta));
  };

  const handleRepsDelta = (delta) => {
    setReps((prev) => Math.max(1, prev + delta));
  };

  // Complete current set
  const handleCompleteSet = () => {
    const isLastSetOfExercise = currentSet >= targetSets;
    const isLastExercise = currentExerciseIdx >= totalExercises - 1;

    completeCurrentSet({
      weight,
      reps,
      targetSets,
      restSeconds: configuredRestSeconds,
    });

    if (isLastSetOfExercise && isLastExercise) {
      handleFinishWorkout();
    }
  };

  // Finish Workout
  const handleFinishWorkout = async () => {
    if (!active) return;
    const weightKg =
      user?.weight_kg ||
      (typeof user?.current_weight === "number"
        ? user.use_lbs === false
          ? user.current_weight
          : user.current_weight * 0.45359237
        : 75);

    const calories = estimateCaloriesBurned(weightKg, elapsedSeconds);
    const todayDate = format(new Date(), "yyyy-MM-dd");
    const endedAt = new Date().toISOString();

    // Calculate total sets logged
    let totalSetsLogged = 0;
    const completedSetsObj = active?.completed_sets || {};
    Object.values(completedSetsObj).forEach((setArr) => {
      if (Array.isArray(setArr)) totalSetsLogged += setArr.length;
    });

    const summaryData = {
      plan_name: active.plan_name,
      duration_seconds: elapsedSeconds,
      calories_burned: calories,
      exercises_completed: totalExercises,
      total_sets: totalSetsLogged + 1,
    };

    setCompletedSummary(summaryData);
    confetti({
      particleCount: 90,
      spread: 75,
      origin: { y: 0.6 },
    });

    // Save to Database
    try {
      await base44.entities.WorkoutSession.create({
        user_id: user?.id,
        workout_plan_id: active.plan_id,
        plan_name: active.plan_name,
        started_at: active.started_at,
        ended_at: endedAt,
        duration_seconds: elapsedSeconds,
        calories_burned: calories,
        exercises_completed: totalExercises,
        total_exercises: totalExercises,
        completed_date: todayDate,
      });

      if (active.plan_id) {
        await base44.entities.WorkoutCompletion.create({
          user_id: user?.id,
          workout_plan_id: active.plan_id,
          completed_date: todayDate,
        });
      }

      // Log ExerciseCompletions
      for (let i = 0; i < exercises.length; i++) {
        const ex = exercises[i];
        const setsList = completedSetsObj[i] || [];
        if (setsList.length > 0) {
          const maxW = Math.max(...setsList.map((s) => Number(s.weight) || 0));
          await base44.entities.ExerciseCompletion.create({
            user_id: user?.id,
            workout_plan_id: active.plan_id,
            exercise_name: ex.name,
            completed_date: todayDate,
            max_weight: maxW > 0 ? maxW : null,
            completed_sets: setsList.length,
            stats: `${setsList.length} sets completed`,
          });
        }
      }
    } catch (_err) {
      /* offline or non-blocking */
    }
  };

  const handleReturnToWorkouts = () => {
    endWorkout();
  };

  // -------------------------------------------------------------------------
  // COMPLETION VIEW
  // -------------------------------------------------------------------------
  if (completedSummary) {
    return (
      <div className="fixed inset-0 z-50 bg-[#050A12] text-white flex flex-col items-center justify-between p-6 select-none animate-in fade-in duration-300">
        <div className="w-full max-w-md mx-auto my-auto text-center space-y-6">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/20 border-2 border-emerald-400 flex items-center justify-center text-emerald-400 shadow-[0_0_30px_rgba(52,211,153,0.3)]">
            <Trophy className="w-10 h-10" />
          </div>

          <div className="space-y-1">
            <p className="text-xs font-bold tracking-widest text-emerald-400 uppercase">
              Workout Complete
            </p>
            <h1 className="text-3xl font-display font-black tracking-tight text-white uppercase">
              {completedSummary.plan_name}
            </h1>
            <p className="text-sm text-slate-400">
              Outstanding session. Progress saved to your account.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 bg-[#101C2E] border border-white/10 rounded-2xl p-4">
            <div className="text-center">
              <Clock className="w-4 h-4 mx-auto mb-1 text-sky-400" />
              <p className="text-xs text-slate-400 font-medium">Duration</p>
              <p className="text-base font-bold font-mono text-white">
                {formatElapsed(completedSummary.duration_seconds)}
              </p>
            </div>
            <div className="text-center border-x border-white/10 px-2">
              <Dumbbell className="w-4 h-4 mx-auto mb-1 text-purple-400" />
              <p className="text-xs text-slate-400 font-medium">Exercises</p>
              <p className="text-base font-bold text-white">
                {completedSummary.exercises_completed}
              </p>
            </div>
            <div className="text-center">
              <Flame className="w-4 h-4 mx-auto mb-1 text-amber-400" />
              <p className="text-xs text-slate-400 font-medium">Calories</p>
              <p className="text-base font-bold text-white">
                {completedSummary.calories_burned}<HealthSourceInfo topic="exercise" />
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleReturnToWorkouts}
            className="w-full py-4 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-bold text-base shadow-[0_0_25px_rgba(14,165,233,0.5)] active:scale-95 transition-all"
          >
            RETURN TO THE WORKOUTS
          </button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // ACTIVE WORKOUT VIEW
  // -------------------------------------------------------------------------
  return (
    <div
      className="fixed inset-0 z-50 bg-[#050A12] text-white flex flex-col overflow-hidden select-none"
      style={{
        height: "100dvh",
        paddingTop: "max(8px, env(safe-area-inset-top))",
        paddingBottom: "max(12px, env(safe-area-inset-bottom))",
      }}
    >
      {/* 1. TOP MINIMAL SESSION BAR */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 shrink-0">
        <button
          type="button"
          onClick={() => setConfirmExitOpen(true)}
          className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/80 hover:text-white transition active:scale-95"
          aria-label="Exit Workout"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 max-w-[55%]">
          <span className="text-xs font-bold uppercase tracking-wider text-white/90 truncate">
            {active?.plan_name || "Workout"}
          </span>
          <span className="text-[10px] font-semibold bg-white/10 text-sky-300 px-1.5 py-0.5 rounded">
            {currentExerciseIdx + 1}/{totalExercises}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-500/10 border border-sky-400/20 text-sky-400">
            <span className="font-mono text-xs font-bold tracking-wider">
              {formatElapsed(elapsedSeconds)}
            </span>
          </div>

          <button
            type="button"
            onClick={togglePauseTimer}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition active:scale-95"
            title={active?.is_paused ? "Resume Timer" : "Pause Timer"}
          >
            {active?.is_paused ? <Play className="w-3.5 h-3.5 text-emerald-400" /> : <Pause className="w-3.5 h-3.5 text-white/80" />}
          </button>
        </div>
      </div>

      {/* 2. UPPER-MIDDLE: EXERCISE DEMONSTRATION GIF (expands to fill available vertical space) */}
      <div className="w-full px-4 py-1.5 flex items-center justify-center flex-1 min-h-[160px] overflow-hidden">
        <div className="w-full h-full rounded-2xl overflow-hidden bg-black/60 border border-white/10 flex items-center justify-center relative shadow-inner">
          {gifUrl ? (
            <img
              src={gifUrl}
              alt={currentExercise?.name || fullExercise?.name || "Exercise demonstration"}
              className="w-full h-full object-contain"
              loading="eager"
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-slate-500 gap-1.5">
              <Dumbbell className="w-10 h-10 opacity-40" />
              <span className="text-xs font-medium">Demo unavailable</span>
            </div>
          )}

          {/* Exercise Index Badge Overlay */}
          <div className="absolute top-2 left-2 bg-[#050A12]/80 backdrop-blur-md px-2 py-0.5 rounded-full border border-white/15 text-[10px] font-bold tracking-wider text-sky-400">
            EXERCISE {currentExerciseIdx + 1} OF {totalExercises}
          </div>

          {/* Muscle tag overlay */}
          {targetMuscles.length > 0 && (
            <div className="absolute top-2 right-2 bg-[#050A12]/80 backdrop-blur-md px-2 py-0.5 rounded-full border border-white/15 text-[10px] font-semibold text-white/80 capitalize">
              {targetMuscles[0]}
            </div>
          )}
        </div>
      </div>

      {/* 3. MIDDLE: EXERCISE INFO & INSTRUCTIONS */}
      <div className="shrink-0 px-4 py-1 flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between shrink-0">
          <h2 className="text-base sm:text-lg font-display font-extrabold capitalize text-white tracking-tight truncate max-w-[80%]">
            {currentExercise?.name || fullExercise?.name || "Exercise"}
          </h2>
          <span className="text-xs text-sky-400 font-semibold shrink-0">
            {targetSets} sets × {prescribedReps}
          </span>
        </div>

        {/* INSTRUCTIONS CONTAINER */}
        <div
          className="bg-[#101C2E]/70 border border-white/10 rounded-2xl flex flex-col shrink-0"
          style={{ padding: instructionsTypography.padding }}
        >
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 shrink-0">
            Instructions
          </p>
          {instructions.length > 0 ? (
            <ol
              className="flex flex-col pr-0.5"
              style={{ gap: instructionsTypography.gap }}
            >
              {instructions.map((step, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span
                    className="text-sky-400 font-bold shrink-0 mt-0.5"
                    style={{
                      minWidth: "16px",
                      fontSize: instructionsTypography.fontSize,
                      lineHeight: instructionsTypography.lineHeight,
                    }}
                  >
                    {idx + 1}.
                  </span>
                  <span
                    className="text-slate-200"
                    style={{
                      fontSize: instructionsTypography.fontSize,
                      lineHeight: instructionsTypography.lineHeight,
                    }}
                  >
                    {step}
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-slate-400 italic text-xs leading-relaxed">
              Maintain control through the full range of motion. Breathe steadily throughout the set.
            </p>
          )}
        </div>
      </div>

      {/* 4. BOTTOM: CURRENT SET CONTROLS OR AUTOMATIC REST TIMER */}
      <div className="px-4 pt-1 pb-2 shrink-0">
        {isResting ? (
          /* AUTOMATIC REST TIMER MODE */
          <div className="bg-gradient-to-b from-[#101C2E] to-[#0A1220] border border-sky-500/30 rounded-2xl p-4 text-center space-y-3 shadow-[0_0_20px_rgba(14,165,233,0.2)] animate-in fade-in duration-200">
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span className="font-bold uppercase tracking-wider text-sky-400 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Rest Period
              </span>
              <span className="text-slate-300">
                Next: Set {restTimer.next_set}
              </span>
            </div>

            <div className="text-4xl sm:text-5xl font-mono font-black text-white tracking-widest">
              {String(Math.floor(restSecondsRemaining / 60)).padStart(2, "0")}:
              {String(restSecondsRemaining % 60).padStart(2, "0")}
            </div>

            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={() => addRestSeconds(30)}
                className="flex-1 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 text-xs font-bold text-sky-300 active:scale-95 transition"
              >
                +30 Seconds
              </button>

              <button
                type="button"
                onClick={skipRest}
                className="flex-1 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-xs font-bold text-white shadow-[0_0_15px_rgba(14,165,233,0.4)] active:scale-95 transition"
              >
                Skip Rest
              </button>
            </div>
          </div>
        ) : (
          /* NORMAL SET INPUT MODE */
          <div className="bg-[#101C2E] border border-white/10 rounded-2xl p-3 space-y-2.5">
            <div className="flex items-center justify-between text-xs">
              <span className="font-bold tracking-wider text-sky-400 uppercase">
                SET {currentSet} OF {targetSets}
              </span>
              <span className="text-slate-400">
                Target: {prescribedReps} reps
              </span>
            </div>

            {/* Steppers Row: Weight & Reps */}
            <div className="grid grid-cols-2 gap-3">
              {/* Weight */}
              <div className="bg-[#050A12]/80 border border-white/10 rounded-xl p-2 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => handleWeightDelta(-5)}
                  className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white active:scale-90"
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <div className="text-center">
                  <span className="text-sm font-bold text-white font-mono">{weight}</span>
                  <span className="text-[10px] text-slate-400 ml-1">{user?.use_lbs ? "lbs" : "kg"}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleWeightDelta(5)}
                  className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white active:scale-90"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Reps */}
              <div className="bg-[#050A12]/80 border border-white/10 rounded-xl p-2 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => handleRepsDelta(-1)}
                  className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white active:scale-90"
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <div className="text-center">
                  <span className="text-sm font-bold text-white font-mono">{reps}</span>
                  <span className="text-[10px] text-slate-400 ml-1">reps</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRepsDelta(1)}
                  className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white active:scale-90"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* COMPLETE SET PRIMARY BUTTON */}
            <button
              type="button"
              onClick={handleCompleteSet}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-extrabold text-sm tracking-wide shadow-[0_0_20px_rgba(14,165,233,0.4)] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              <CheckCircle2 className="w-4 h-4" />
              {currentSet >= targetSets && currentExerciseIdx >= totalExercises - 1
                ? "FINISH LAST SET"
                : `COMPLETE SET ${currentSet}`}
            </button>

            {/* Previous / Skip navigation */}
            <div className="flex items-center justify-between pt-0.5 text-xs text-slate-400">
              {currentExerciseIdx > 0 ? (
                <button
                  type="button"
                  onClick={goToPreviousExercise}
                  className="flex items-center gap-1 hover:text-white transition"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Previous Exercise
                </button>
              ) : (
                <span />
              )}

              {currentExerciseIdx < totalExercises - 1 && (
                <button
                  type="button"
                  onClick={goToNextExercise}
                  className="flex items-center gap-1 hover:text-white transition"
                >
                  Skip Exercise <FastForward className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* CONFIRM EXIT OVERLAY */}
      {confirmExitOpen && (
        <div className="absolute inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in duration-200">
          <div className="bg-[#101C2E] border border-white/15 rounded-2xl p-5 max-w-sm w-full space-y-4 shadow-2xl text-center">
            <h3 className="text-xl font-bold text-white">Leave Workout?</h3>
            <p className="text-xs text-slate-300 leading-relaxed">
              Your workout progress is saved. You can resume anytime right where you left off.
            </p>
            <div className="space-y-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setConfirmExitOpen(false);
                  saveAndExit();
                }}
                className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-bold text-sm shadow-[0_0_15px_rgba(14,165,233,0.4)] active:scale-95 transition"
              >
                Save & Exit
              </button>
              <button
                type="button"
                onClick={() => setConfirmExitOpen(false)}
                className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm active:scale-95 transition"
              >
                Continue Workout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
